import React, { useEffect, useState } from 'react';
import { Search, Building2, Anchor, Plane, Edit2, Trash2 } from 'lucide-react';
import { MasterContactOrganizationDTO, WebhookConfigDTO } from './type';
import { axiosInstance } from '../../utility/apiInstance';
import toast from 'react-hot-toast';
import SearchInput from '../../common/SearchInput';

const OrganizationWebhookSelector = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedOrg, setSelectedOrg] = useState<MasterContactOrganizationDTO | null>();
    const [webhooks, setWebhooks] = useState({
        air: '',
        ocean: ''
    });
    const [savedWebhooks, setSavedWebhooks] = useState<WebhookConfigDTO[]>([]);
    const [organizations, setOrganizations] = useState<MasterContactOrganizationDTO[]>([]);
    const [selectedWebhookConfig, setSelectedWebhookConfig] = useState<String | null>(null);
    const [listSearchTerm, setListSearchTerm] = useState('');
    const [errors, setErrors] = useState<{
        airWebhook?: string;
        oceanWebhook?: string;
    }>({});
    const [deleteConfirm, setDeleteConfirm] = useState<{
        open: boolean;
        webhookId: string | null;
        orgName?: string;
    }>({
        open: false,
        webhookId: null,
    });

    const [editDataId, setEditDataId] = useState('');


    const getMasterContactOrganizations = async () => {
        try {
            const { data } = await axiosInstance.get(`api/v1/master-contact-organization`);
            setOrganizations(data.data);
        } catch (error) {
            setOrganizations([]);
        }
    }

    const getWebhookConfigByOrgService = async () => {
        try {
            const { data } = await axiosInstance.get(`api/v1/webhook-config`);
            setSavedWebhooks(data.data);
        } catch (error) {
            setSavedWebhooks([]);
        }
    }


    const filteredSavedWebhooks = savedWebhooks.filter(webhook =>
        webhook.organizationName.toLowerCase().includes(listSearchTerm.toLowerCase())
    );



    useEffect(() => {
        getWebhookConfigByOrgService();
        getMasterContactOrganizations();
    }, [])
    const filteredOrgs = organizations.filter(org =>
        org.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleWebhookChange = (type: any, value: any) => {
        // setWebhooks(prev => ({ ...prev, [type]: value }));
        setWebhooks(prev => ({ ...prev, [type]: value }));

        // clear error while typing
        setErrors(prev => ({
            ...prev,
            [type === 'air' ? 'airWebhook' : 'oceanWebhook']: undefined
        }));
    };

    const handleOrgSelect = (org: MasterContactOrganizationDTO) => {
        const existing = savedWebhooks.find(w => w.masterContactOrganization === org._id);
        setSelectedOrg(org);
        setSearchTerm('');
        if (existing) {
            setWebhooks({
                air: existing.airWebhook || '',
                ocean: existing.oceanWebhook || ''
            });
        } else {
            setWebhooks({ air: '', ocean: '' });
        }
    }
    const handleSave = async () => {
        try {
            setErrors({});

            if (!selectedWebhookConfig) {
                const newEntry = {
                    masterContactOrganization: selectedOrg?._id,
                    organizationName: selectedOrg?.name,
                    airWebhook: webhooks.air,
                    oceanWebhook: webhooks.ocean
                };

                await axiosInstance.post(`api/v1/webhook-config`, newEntry);
                toast.success('Webhook created successfully');
            } else {
                const payload = {
                    airWebhook: webhooks.air,
                    oceanWebhook: webhooks.ocean
                };

                await axiosInstance.put(
                    `api/v1/webhook-config/${selectedWebhookConfig}`,
                    payload
                );
                toast.success('Webhook updated successfully');
            }

            setSelectedOrg(null);
            setEditDataId("");
            setWebhooks({ air: '', ocean: '' });
            getWebhookConfigByOrgService();

        } catch (error: any) {
            const apiErrors = error.response?.data?.error;

            // 🔥 FIELD LEVEL ERROR HANDLING
            if (Array.isArray(apiErrors)) {
                const fieldErrors: any = {};

                apiErrors.forEach((err: any) => {
                    fieldErrors[err.field] = err.message;
                });

                setErrors(fieldErrors);
                return;
            }

            // fallback
            toast.error(
                error.response?.data?.message || 'Failed to add webhook'
            );
        }
    };


    const handleEdit = (webhook: WebhookConfigDTO) => {
        setEditDataId(webhook._id)
        const org = organizations.find(o => o._id === webhook.masterContactOrganization);
        setSelectedOrg(org);
        setSelectedWebhookConfig(webhook._id);
        setWebhooks({
            air: webhook.airWebhook || '',
            ocean: webhook.oceanWebhook || ''
        });
    };


    const handleDelete = async (webhookId: string) => {
        try {
            const { data } = await axiosInstance.delete(`api/v1/webhook-config/${webhookId}`);
            toast.success('Webhook deleted successfully');
            getWebhookConfigByOrgService();
            return;
        } catch (error: any) {
            const errorMes = error.response?.data?.error || 'Failed to delete webhook';
            toast.error(errorMes);
            return;
        }
    };

    return (
        <div className="manageProfileMain">
            <div>

                {/* Add/Edit Configuration Card */}
                <div className="addOrganizationWebhookOuter">
                    <div className="addOrganizationWebhookHead">
                        <span><Building2 /></span>
                        <h2>
                            {selectedOrg ? 'Edit' : 'Add'} Organization Webhook Configuration
                        </h2>
                    </div>

                    {/* Organization Search & Select */}
                    <div className="addOrganizationWebhookForm">
                        <div className="form-group">
                            <label className="control-label">
                                Organization
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    placeholder="Search and select organization..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-control"

                                />

                            </div>

                            {/* Dropdown List */}
                            {searchTerm && (
                                <div style={{
                                    marginTop: '4px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    backgroundColor: 'white',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    position: 'absolute',
                                    zIndex: 10,
                                    width: 'calc(91% - 48px)',
                                    maxWidth: '1352px'
                                }}>
                                    {filteredOrgs.map(org => (
                                        <div
                                            key={org._id}
                                            onClick={() => handleOrgSelect(org)}
                                            style={{
                                                padding: '10px 12px',
                                                cursor: 'pointer',
                                                borderBottom: '1px solid #f0f0f0',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                            <div style={{ fontWeight: '500', fontSize: '13px', color: '#2c3e50' }}>
                                                {org.name}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '2px' }}>
                                                {org.type}
                                            </div>
                                        </div>
                                    ))}
                                    {filteredOrgs.length === 0 && (
                                        <div style={{
                                            padding: '16px',
                                            color: '#95a5a6',
                                            textAlign: 'center',
                                            fontSize: '13px'
                                        }}>
                                            No organizations found
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Selected Organization Display */}
                            {selectedOrg && (
                                <div style={{
                                    margin: '12px 0',
                                    padding: '12px',
                                    backgroundColor: '#fff3cd',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: '1px solid #ffeaa7'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: '500', fontSize: '13px', color: '#2c3e50' }}>
                                            {selectedOrg.name}
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#856404', marginTop: '2px' }}>
                                            {selectedOrg.type}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setSelectedOrg(null);
                                            setWebhooks({ air: '', ocean: '' });
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#856404',
                                            cursor: 'pointer',
                                            fontSize: '20px',
                                            padding: '0 8px',
                                            lineHeight: '1'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Webhook Configuration */}
                    {selectedOrg && (
                        <>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '16px',
                                marginBottom: '20px'
                            }}>
                                {/* Air Milestone Webhook */}
                                <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '8px',
                                        fontSize: '13px',
                                        color: '#555',
                                        fontWeight: '500'
                                    }}>
                                        <Plane size={14} style={{ marginRight: '6px', color: '#3498db' }} />
                                        Air Milestone Update Webhook
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Enter valid webhook URL"
                                        value={webhooks.air}
                                        onChange={(e) => handleWebhookChange('air', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '13px',
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            backgroundColor: '#f8f9fa'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f39c12';
                                            e.target.style.backgroundColor = 'white';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#ddd';
                                            e.target.style.backgroundColor = '#f8f9fa';
                                        }}
                                    />
                                    {errors.airWebhook && (
                                        <div style={{
                                            marginTop: '6px',
                                            fontSize: '12px',
                                            color: '#e74c3c'
                                        }}>
                                            {errors.airWebhook}
                                        </div>
                                    )}
                                </div>

                                {/* Ocean Milestone Webhook */}
                                <div>
                                    <label style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '8px',
                                        fontSize: '13px',
                                        color: '#555',
                                        fontWeight: '500'
                                    }}>
                                        <Anchor size={14} style={{ marginRight: '6px', color: '#3498db' }} />
                                        Ocean Milestone Update Webhook
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Enter valid webhook URL"
                                        value={webhooks.ocean}
                                        onChange={(e) => handleWebhookChange('ocean', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: '13px',
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            backgroundColor: '#f8f9fa'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#f39c12';
                                            e.target.style.backgroundColor = 'white';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#ddd';
                                            e.target.style.backgroundColor = '#f8f9fa';
                                        }}
                                    />
                                    {errors.oceanWebhook && (
                                        <div style={{
                                            marginTop: '6px',
                                            fontSize: '12px',
                                            color: '#e74c3c'
                                        }}>
                                            {errors.oceanWebhook}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={handleSave}
                                disabled={!webhooks.air && !webhooks.ocean}
                                className="btn btnTheme"

                            >
                                Save Configuration
                            </button>
                        </>
                    )}
                </div>

                <div className="configuredWebhooksListMain">
                    <div className="configuredWebhooksHead">
                        <h2>Configured Webhooks</h2>
                        <SearchInput
                            value={listSearchTerm}
                            onChange={setListSearchTerm}
                            placeholder="Search by organization..."
                        />
                    </div>
                    {filteredSavedWebhooks.length === 0 ? (
                        <div style={{
                            textAlign: 'center',
                            padding: '40px 20px',
                            color: '#95a5a6'
                        }}>
                            <Building2 size={40} style={{ color: '#ddd', marginBottom: '12px' }} />
                            <p style={{ fontSize: '14px', margin: '0' }}>
                                No webhook configurations yet
                            </p>
                        </div>
                    ) : (
                        <div className="configuredWebhooksListContainer">
                            {filteredSavedWebhooks.map(webhook => (
                                <div
                                    key={webhook._id}
                                    className="configuredWebhooksList"

                                >

                                    <div className="configuredWebhooksListHead">
                                        <div className="configuredWebhooksListHeadLeft">
                                            <span><Building2 size={16} /></span>
                                            <h3>{webhook.organizationName}</h3>
                                        </div>



                                        {/* Action Buttons */}
                                        <div className="WebhooksButtons">
                                            <button
                                                disabled={editDataId === webhook._id}
                                                onClick={() => handleEdit(webhook)}
                                                className="btn btnYellow"

                                            >
                                                <Edit2 size={12} />
                                                Edit
                                            </button>
                                            <button
                                                disabled={editDataId === webhook._id}
                                                onClick={() =>
                                                    setDeleteConfirm({
                                                        open: true,
                                                        webhookId: webhook._id,
                                                        orgName: webhook.organizationName,
                                                    })
                                                }
                                                className="btn btnBlack"

                                            >
                                                <Trash2 size={12} />
                                                Delete
                                            </button>
                                        </div>
                                    </div>

                                    <div className="configuredWebhooksListForm">
                                        {/* Air Webhook */}
                                        <div className="form-group">
                                            <label className="control-label">
                                                <Plane size={14} />
                                                Air Webhook
                                            </label>
                                            <div style={{
                                                fontSize: '12px',
                                                color: webhook.airWebhook ? '#2c3e50' : '#95a5a6',
                                                padding: '8px',
                                                backgroundColor: 'white',
                                                borderRadius: '3px',
                                                border: '1px solid #e8e8e8',
                                                wordBreak: 'break-all'
                                            }}>
                                                {webhook.airWebhook || 'Not configured'}
                                            </div>
                                        </div>

                                        {/* Ocean Webhook */}
                                        <div className="form-group">
                                            <label className="control-label">
                                                <Anchor size={14} />
                                                Ocean Webhook
                                            </label>
                                            <div style={{
                                                fontSize: '12px',
                                                color: webhook.oceanWebhook ? '#2c3e50' : '#95a5a6',
                                                padding: '8px',
                                                backgroundColor: 'white',
                                                borderRadius: '3px',
                                                border: '1px solid #e8e8e8',
                                                wordBreak: 'break-all'
                                            }}>
                                                {webhook.oceanWebhook || 'Not configured'}
                                            </div>
                                        </div>
                                    </div>


                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {deleteConfirm.open && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '6px',
                            width: '360px',
                            padding: '24px',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                        }}
                    >
                        <h3
                            style={{
                                margin: '0 0 12px 0',
                                fontSize: '16px',
                                color: '#2c3e50',
                                fontWeight: '600',
                            }}
                        >
                            Are you sure?
                        </h3>

                        <p
                            style={{
                                fontSize: '13px',
                                color: '#555',
                                marginBottom: '20px',
                                lineHeight: '1.5',
                            }}
                        >
                            Do you really want to delete the webhook configuration for{' '}
                            <strong>{deleteConfirm.orgName}</strong>?
                            This action cannot be undone.
                        </p>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                onClick={() =>
                                    setDeleteConfirm({ open: false, webhookId: null })
                                }
                                style={{
                                    padding: '8px 14px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    backgroundColor: 'white',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={() => {
                                    deleteConfirm?.webhookId && handleDelete(deleteConfirm.webhookId);
                                    setDeleteConfirm({ open: false, webhookId: null });
                                }}
                                style={{
                                    padding: '8px 14px',
                                    borderRadius: '4px',
                                    border: 'none',
                                    backgroundColor: '#e74c3c',
                                    color: 'white',
                                    fontSize: '13px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OrganizationWebhookSelector;